import React, { useState, useRef, useEffect } from "react";

import { Row, Col } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import { useStateMachine } from "little-state-machine";

import { getShipperCompanyDetails, updateShipperCompanyDetails, getCountries } from "api";
import { ReactComponent as ParcelBox } from "assets/images/icons/ParcelBox.svg";
import Button from "components/atoms/Button/Button";
import Input from "components/atoms/Input/Input";
import { isValueExist } from "helpers/validationHelpers";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import errorToast from "helpers/errorToast";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import { updateCompanyName } from "store/store";
import usePermissions from "hooks/usePermissions";

interface ICompanyDetails {
  company_address: string;
  company_name: string;
  country_code: string;
  country_name: string;
  logistics_telephone_number: string;
  logo?: File;
  website: string;
  delete_logo: boolean;
}

function CompanyDetailsForm() {
  const intl = useIntl();
  const methods = useForm<ICompanyDetails>({ shouldFocusError: true });
  const { setValue, handleSubmit } = methods;
  const { actions } = useStateMachine({ updateCompanyName });
  const { permissions } = usePermissions();
  const {
    permissions: {
      admin_settings: { edit_company_settings },
    },
  } = JSON.parse(permissions || "{}");

  const [isLoading, setIsLoading] = useState(false);

  const [countryOptions, setCountryOptions] = useState<any>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyLogo, setCompanyLogo] = useState<any>();
  const [updatedCompanyLogo, setUpdatedCompanyLogo] = useState<string>();

  const renderParcelBox = () => (
    <div
      className="d-flex align-items-center justify-content-center input-border-grey"
      style={{ height: "64px", width: "64px", borderRadius: "8px" }}
    >
      <ParcelBox height="35" width="35" />
    </div>
  );

  const FileUploader = function () {
    const hiddenFileInput = useRef<HTMLInputElement>(null);
    const handleClick = () => {
      if (hiddenFileInput.current) {
        hiddenFileInput.current.click();
      }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files || !event.target.files[0]) return;
      const file = event.target.files[0];
      if (companyLogo) setUpdatedCompanyLogo(file.name);
      if (!companyLogo) setCompanyLogo(file.name);
      setValue("logo", file);
    };

    return (
      <div className="d-flex pl-15">
        <Button onClick={handleClick} variant="primary" className="no-wrap btn-secondary" disabled={!edit_company_settings}>
          {companyLogo ? (
            <FormattedMessage id="settings.company-details.replaceAvatar" defaultMessage="Replace avatar" />
          ) : (
            <FormattedMessage id="new-request.packing-list.addFile" defaultMessage="Add avatar" />
          )}
        </Button>
        {companyLogo && (
          <Button
            variant="danger"
            className="no-wrap ml-2"
            onClick={() => {
              setCompanyLogo("");
              setUpdatedCompanyLogo("");
            }}
            disabled={!edit_company_settings}
          >
            <FormattedMessage id="settings.company-details.removeAvatar" defaultMessage="Remove avatar" />
          </Button>
        )}

        <input
          type="file"
          ref={hiddenFileInput}
          onChange={handleChange}
          className="d-none"
          accept="image/png, image/gif, image/jpeg"
        />
      </div>
    );
  };

  const getCompanyDetails = () => {
    setIsLoading(true);
    getShipperCompanyDetails()
      .then((res) => {
        if (res.status === 200) {
          actions.updateCompanyName({
            companyName: res.data.company_name,
          });
          setValue("company_name", res.data.company_name);
          setValue("company_address", res.data.company_address);
          setValue("logistics_telephone_number", res.data.logistics_telephone_number);
          setValue("website", res.data.website);
          setValue("country_code", res.data.country_code);
          if (res.data.logo) setCompanyLogo(res.data.logo);
        }
      })
      .catch((err) => {
        errorToast(err);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    setIsLoading(true);
    getCompanyDetails();
    getCountries()
      .then((res) => setCountryOptions(res.data))
      .catch((err) => errorToast(err))
      .finally(() => setIsLoading(false));
  }, []);

  const onSubmit = (data: ICompanyDetails) => {
    setIsSubmitting(true);
    const formData = new FormData();
    if (data.logo && data.logo.size > 2097152) {
      errorToast(
        null,
        intl.formatMessage({
          id: "general.fileSizeError",
          defaultMessage: "File size is too large. Maximum file size is 2MB.",
        }),
      );
      setIsSubmitting(false);
      return;
    }
    if (data.logo) formData.append("logo", data.logo);
    formData.append("company_name", data.company_name);
    formData.append("country_code", data.country_code);
    formData.append("company_address", data.company_address);
    formData.append("logistics_telephone_number", data.logistics_telephone_number);
    formData.append("website", data.website);
    formData.append("delete_logo", companyLogo ? "false" : "true");

    updateShipperCompanyDetails(formData)
      .then((res) => {
        if (res.status === 200) {
          toast(
            intl.formatMessage({
              id: "settings.settingsUpdated",
              defaultMessage: "Settings updated successfully",
            }),
            {
              type: "success",
            },
          );
          setUpdatedCompanyLogo("");
          getCompanyDetails();
        }
      })
      .catch((err) => errorToast(err))
      .finally(() => setIsSubmitting(false));
  };

  if (isLoading) return <LoadingSkeleton rows={2} />;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row
          style={{
            padding: "16px 0",
          }}
        >
          <Col sm={3}>
            <div className="font-weight-bold font-b1">
              <FormattedMessage id="settings.companyDetails" defaultMessage="Company Details" />
            </div>
            <span className=" font-b3">
              <FormattedMessage
                id="settings.company-details.companyDetailsSubtext"
                defaultMessage="  Please provide the company details"
              />
            </span>
          </Col>
          <Col sm={9} className="d-flex flex-column font-b3">
            <div className="d-flex flex-column mb-3" style={{ width: "100%", maxWidth: "392px" }}>
              <div className="mb-1 font-b3">
                <FormattedMessage id="general.companyName" defaultMessage="Company name" />
              </div>
              <Input
                required
                type="text"
                name="company_name"
                eventContext="Settings - Company Details"
                className="rounded-input w-100"
                disabled
                validations={{
                  validate: { valueExist: isValueExist },
                }}
              />
            </div>
            <div className="mb-3 d-flex flex-wrap">
              <div className="mr-2 mb-xs-2">
                <div className="mb-1 font-b3 required">
                  <FormattedMessage id="settings.company-details.mainAddress" defaultMessage="Main address" />
                </div>
                <Input
                  required
                  type="text"
                  name="company_address"
                  eventContext="Settings - Company Details"
                  className="rounded-input input-width-md"
                  validations={{
                    validate: { valueExist: isValueExist },
                  }}
                  disabled={!edit_company_settings}
                />
              </div>
              <div>
                <div className="mb-1 font-b3 required">
                  <FormattedMessage id="general.country" defaultMessage="Country" />
                </div>
                <Dropdown
                  required
                  useHookForm
                  options={countryOptions}
                  name="country_code"
                  className="w-192"
                  disabled={!edit_company_settings}
                />
              </div>
            </div>

            <div className="d-flex flex-wrap mb-3">
              <div className="mr-2 mb-xs-2">
                <div className="mb-1 font-b3 required">
                  <FormattedMessage id="settings.contact-list.phone.placeholder" defaultMessage="Phone Number" />
                </div>
                <Input
                  required
                  type="text"
                  name="logistics_telephone_number"
                  eventContext="Settings - Company Details"
                  className="rounded-input input-width-md"
                  validations={{
                    validate: { valueExist: isValueExist },
                  }}
                  disabled={!edit_company_settings}
                />
              </div>
              <div>
                <div className="mb-1 font-b3 required">
                  <FormattedMessage id="settings.company-details.website" defaultMessage="Website" />
                </div>
                <Input
                  required
                  type="text"
                  className="input-width-md rounded-input"
                  name="website"
                  eventContext="Settings - Company Details"
                  validations={{
                    validate: { valueExist: isValueExist },
                  }}
                  disabled={!edit_company_settings}
                />
              </div>
            </div>

            <div className="m-0 d-flex align-items-center mt-2">
              <div className="d-flex pr-15 align-items-center justify-content-between">
                {companyLogo && !updatedCompanyLogo && (
                  <div className="d-flex" style={{ width: "40px" }}>
                    {companyLogo.startsWith("https") ? (
                      <img src={companyLogo} alt="Company logo" width="64" height="64" />
                    ) : (
                      <span>{companyLogo}</span>
                    )}
                  </div>
                )}
                {!companyLogo && <div>{renderParcelBox()}</div>}
              </div>
              {updatedCompanyLogo && (
                <div className="d-flex align-items-center" style={{ transform: "translateX(-12px)" }}>
                  <span className="ml-2 m-0 p-0">{updatedCompanyLogo}</span>
                </div>
              )}

              <FileUploader />
            </div>
            <div className="mt-3">
              <Button
                style={{
                  width: "106px",
                  whiteSpace: "nowrap",
                }}
                className="btn-secondary"
                onClick={handleSubmit(onSubmit)}
                isLoading={isSubmitting}
                disabled={!edit_company_settings}
              >
                <FormattedMessage id="general.saveChanges" defaultMessage="Save changes" />
              </Button>
            </div>
          </Col>
        </Row>
      </form>
    </FormProvider>
  );
}

export default CompanyDetailsForm;
